/**
 * Emit auto-links describing the build-time relationships between members
 * of a freshly-assembled vanity fixture group.
 *
 * Pure — no I/O, no mutation. Inspects `groupObjects` (one fixture group's
 * world-space objects) and returns an array of `groupCustomization.links`
 * entries with `source: 'auto'` so the editor can distinguish them from
 * user-drawn links.
 *
 * The link blueprint:
 *
 *   faucet → mirror             rp        (or cabinet → mirror if no faucet)
 *   mirror → overhead light     rp
 *   mirror → ceiling light      rp
 *   sconce pair around mirror   centerLine  rotationMode: 'mirror'
 *   single sconce               rp        mirror → sconce
 *   two mirrors (double vanity) copy
 *   two sinks  (double vanity)  copy
 *   sink + whatever shares its   rail-x    one per sink; the only rule that
 *     centerline                           survives a missing mirror
 *
 * Everything above the double-sink pair is the ACCESSORY half, and it lives
 * in `emitAccessoryLinks.js` because the cabinet-run emitter needs the same
 * rules — `mergeLinks` regenerates auto-links wholesale, so whichever emitter
 * runs last owns the record and neither may know less than the other. What
 * remains here is what is particular to a vanity ASSEMBLY: the double-sink
 * pair and the centreline spine.
 *
 * Anchor = vanity cabinet (matches `pickMirrorParents` / `pickLightParents`
 * fallback and the editor's `resolveEditAnchor` preference). All offsets are
 * computed in the source's local frame with `rotationMode: 'copy'`, matching
 * `snapshotGroupCustomization`'s invariant check.
 */

const {
  createLink,
  buildAnchorLocal,
  nearestByDx,
  isWallMounted,
  mountedWallId,
} = require('../group-customization');
const {
  getVanityCabinet,
  getFixtureGroupSinks,
  getFixtureGroupFaucets,
  getFixtureGroupMirrors,
  getFixtureGroupCeilingLights,
  getFixtureGroupOverheadLights,
} = require('./fixtureGroup');
const { emitAccessoryLinks } = require('./emitAccessoryLinks');

/**
 * Anchor-local dx within which two members count as sharing a centerline.
 *
 * Measured, not chosen: across the dev corpus's single-sink vanities, 126/127
 * faucets, 111/112 mirrors and 72/75 overhead lights sit within 0.5" of their
 * sink's centerline, and every outlier is 2.2"-11.2". Nothing lands in
 * between, so anything from 0.5" to 2" gives identical verdicts — the value is
 * the wide side of a bimodal split, not a tuned threshold.
 *
 * Lives here rather than in `accessoryRules.js` because it is an INSPECTION
 * tolerance for reading built geometry, not a placement rule the builder obeys.
 */
const CENTERLINE_TOL_M = 0.0127; // 0.5"

/**
 * @param {Object[]} groupObjects — world-space objects belonging to one
 *   fixture group (already filtered by `fixtureGroupId`).
 * @param {Object} [parentWall] — reserved; not used today. Threaded for
 *   forward compatibility so the signature matches the plan.
 * @returns {Object[]} links suitable for `groupCustomization.links`. Empty
 *   when the group has no cabinet. A group with no MIRROR is not empty: the
 *   mirror-parent rules below no-op on their own (they iterate `mirrors`),
 *   while the centerline spine and the double-vanity pairs still describe
 *   real relationships. A mirror dropped for want of wall space used to take
 *   the whole link graph with it.
 */
// eslint-disable-next-line no-unused-vars
function emitVanityLinks(groupObjects, parentWall) {
  if (!Array.isArray(groupObjects) || groupObjects.length === 0) return [];

  const cabinet = getVanityCabinet(groupObjects);
  if (!cabinet) return [];

  // `getFixtureGroup*` filter by fixtureGroupId; here we already have a
  // single group's objects, so we apply the same predicates inline through
  // a fake-group-id pass: pick whichever group id every member shares (or
  // null) so the helpers don't drop everything.
  const fixtureGroupId = cabinet.fixtureGroupId ?? null;
  const sinks = getFixtureGroupSinks(groupObjects, fixtureGroupId);
  const faucets = getFixtureGroupFaucets(groupObjects, fixtureGroupId);
  const mirrors = getFixtureGroupMirrors(groupObjects, fixtureGroupId);
  const overheads = getFixtureGroupOverheadLights(groupObjects, fixtureGroupId);
  const ceilingLights = getFixtureGroupCeilingLights(
    groupObjects,
    fixtureGroupId
  );

  // Anchor-local pose for every member that might be referenced as a link
  // source or target. Include the cabinet so a fallback cabinet→mirror rp
  // can look up its zero pose by id.
  const localById = new Map();
  for (const o of [
    cabinet,
    ...sinks,
    ...faucets,
    ...mirrors,
    ...overheads,
    ...ceilingLights,
  ]) {
    if (!o?.id) continue;
    if (localById.has(o.id)) continue;
    const local = buildAnchorLocal(o, cabinet);
    if (local) localById.set(o.id, local);
  }

  // Rules 1-4 — mirror parents, the lights above them, the sconces beside
  // them — are `emitAccessoryLinks`, shared with the cabinet-run emitter.
  const accessories = emitAccessoryLinks({
    groupObjects,
    anchor: cabinet,
    mirrorSources: faucets,
  });
  const links = [...accessories.links];

  // 5) Double-vanity sink pair.
  if (sinks.length === 2) {
    const sinkLocals = sinks
      .map((s) => localById.get(s.id))
      .filter(Boolean)
      .sort((a, b) => a.dx - b.dx);
    if (sinkLocals.length === 2) {
      const link = createLink('copy', {
        source: 'auto',
        parentAId: sinkLocals[0].id,
        parentBId: sinkLocals[1].id,
      });
      if (link) links.push(link);
    }
  }

  // 7) Centerline spine. A vanity's sink, faucet, mirror and centered light
  //    share one vertical line — that is a rail-x, and it is the only rule
  //    here that survives a missing mirror.
  //
  //    Emitted LAST because it must see which pairs rules 1-4 already own.
  //    An rp bakes the pair's CURRENT dx offset, and "aligned" carries up to
  //    ~5 mm of real noise, while the solver's TRANSLATION_EPS is 0.5 mm — so
  //    a pair holding both an rp and a rail disagrees during a drag and
  //    `solveDrag` returns `{ reject: true }`, leaving the vanity undraggable.
  //    Excluding every rp TARGET keeps the two constraint sets disjoint.
  if (sinks.length > 0) {
    const sinkLocals = sinks.map((s) => localById.get(s.id)).filter(Boolean);
    const rpTargets = new Set(
      links.filter((l) => l.type === 'rp').map((l) => l.targetId)
    );
    // Sconces are deliberately OFF the centerline (they flank the mirror);
    // rule 4 owns them.
    const candidates = [...faucets, ...mirrors, ...overheads, ...ceilingLights]
      .map((o) => localById.get(o.id))
      .filter((c) => c && !rpTargets.has(c.id));

    // Cluster by nearest sink: a double vanity has TWO centerlines, and one
    // rail over both would yank the sinks together on the first drag.
    const bySinkId = new Map();
    for (const cand of candidates) {
      const sinkLocal = nearestByDx(cand, sinkLocals);
      if (!sinkLocal) continue;
      if (Math.abs((cand.dx || 0) - (sinkLocal.dx || 0)) > CENTERLINE_TOL_M) {
        continue;
      }
      if (!bySinkId.has(sinkLocal.id)) bySinkId.set(sinkLocal.id, []);
      bySinkId.get(sinkLocal.id).push(cand);
    }

    for (const sinkLocal of sinkLocals) {
      const members = bySinkId.get(sinkLocal.id) || [];
      if (members.length === 0) continue; // a lone sink is not a rail
      const link = createLink('rail-x', {
        source: 'auto',
        memberIds: [sinkLocal.id, ...members.map((m) => m.id)],
      });
      if (link) links.push(link);
    }
  }

  // 7) Wall mounting, stated. Every wall-hung member (mirror, sconce,
  //    wall-mount sink/faucet) that knows its wall gets an on-wall link so
  //    the solver pins it to that plane by record rather than by inferring
  //    it from the object's type.
  for (const o of groupObjects) {
    if (!o?.id || !isWallMounted(o)) continue;
    const wallId = mountedWallId(o);
    if (!wallId) continue;
    const link = createLink('on-wall', {
      source: 'auto',
      subjectId: o.id,
      wall: { kind: 'wall', id: wallId },
    });
    if (link) links.push(link);
  }

  return links.filter(Boolean);
}

module.exports = {
  emitVanityLinks,
  CENTERLINE_TOL_M,
  // Re-exported for this module's unit tests; both now live in
  // group-customization/anchorPose so every link emitter shares them.
  buildAnchorLocal,
  nearestByDx,
};
